import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PermissionGate } from '@/components/PermissionGate';
import UniversalFilterBar from '@/components/procurement/UniversalFilterBar';
import MciPackageDrawer from '@/components/procurement/MciPackageDrawer';
import VendorFormDialog from '@/components/procurement/VendorFormDialog';
import ExcelWorkflowMenu from '@/components/procurement/ExcelWorkflowMenu';
import {
  Search, RefreshCw, Mail, Phone, User, Building2, ExternalLink,
  Package, ChevronRight, Save, X, Loader2, BookUser, Copy, AlertTriangle, Plus, Edit,
} from 'lucide-react';

const fmtCurrency = (n) => {
  if (n == null || isNaN(n)) return '—';
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}k`;
  return `$${Number(n).toLocaleString()}`;
};

const STATUS_COLORS = {
  DELAYED:  { bg: 'var(--tone-red-bg)', color: 'var(--tone-red-text)' },
  AT_RISK:  { bg: 'var(--tone-amber-bg)', color: 'var(--tone-amber-text)' },
  ON_TRACK: { bg: 'var(--tone-green-bg)', color: 'var(--tone-green-text)' },
  DONE:     { bg: 'var(--tone-blue-bg)', color: 'var(--tone-blue-text)' },
};

function ContactEditor({ vendor, onClose, onSaved }) {
  const [name, setName]   = useState(vendor.primaryContact?.name  || '');
  const [email, setEmail] = useState(vendor.primaryContact?.email || '');
  const [phone, setPhone] = useState(vendor.primaryContact?.phone || '');
  const [applyToOpen, setApplyToOpen] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const projectId = vendor._projectId;
      await api.put(
        `/v1/projects/${projectId}/mci/control-tower/vendor-contact-book/${encodeURIComponent(vendor.vendor)}/contact`,
        { name, email, phone, applyToOpenMcis: applyToOpen }
      );
      toast.success(`Contact updated${applyToOpen ? ` on ${vendor.mciCount} MCIs` : ''}`);
      onSaved(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update contact');
    }
    setSaving(false);
  };

  const input = "w-full px-2 py-1.5 rounded-md text-sm border";
  const sty = { background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' };
  const lbl = "text-[10px] uppercase font-semibold block mb-1";
  const lblSty = { color: 'var(--text-muted)' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.55)' }}
      data-testid="contact-editor">
      <div className="w-full max-w-md rounded-xl p-5 shadow-2xl space-y-3"
        style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
            Edit Primary Contact — {vendor.vendor}
          </h3>
          <button onClick={onClose}><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
        <div><label className={lbl} style={lblSty}>Contact Name</label>
          <input value={name} onChange={e => setName(e.target.value)} className={input} style={sty}
            data-testid="contact-name-input" /></div>
        <div><label className={lbl} style={lblSty}>Contact Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={input} style={sty}
            data-testid="contact-email-input" /></div>
        <div><label className={lbl} style={lblSty}>Contact Phone</label>
          <input value={phone} onChange={e => setPhone(e.target.value)} className={input} style={sty}
            data-testid="contact-phone-input" /></div>
        <label className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--text-primary)' }}>
          <input type="checkbox" checked={applyToOpen} onChange={e => setApplyToOpen(e.target.checked)}
            data-testid="apply-to-open-mcis" />
          Apply to all {vendor.mciCount} open MCI(s) for this vendor
        </label>
        <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-contact-btn">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            <Save className="w-3.5 h-3.5 mr-1" /> Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function VendorDetailDrawer({ vendor, projectId, onClose, onEdit, onOpenMci }) {
  const copy = (v) => {
    if (!v) return;
    navigator.clipboard.writeText(v);
    toast.success('Copied');
  };

  const otdColor = vendor.otdPercent >= 80 ? '#16A34A' : vendor.otdPercent >= 60 ? '#EA580C' : '#DC2626';

  return (
    <div className="fixed inset-0 z-40 flex justify-end" style={{ background: 'rgba(0,0,0,0.4)' }}
      onClick={onClose} data-testid="vendor-detail-drawer">
      <div className="w-full max-w-2xl h-full overflow-y-auto" onClick={e => e.stopPropagation()}
        style={{ background: 'var(--card-bg)', borderLeft: '1px solid var(--border-color)' }}>
        {/* Header */}
        <div className="sticky top-0 z-10 px-5 py-4 border-b flex items-center justify-between"
          style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <div>
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4" style={{ color: 'var(--tone-navy-text)' }} />
              <h3 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{vendor.vendor}</h3>
            </div>
            <div className="flex gap-2 mt-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
              <span>{vendor.mciCount} MCI(s)</span>·
              <span>{vendor.totalPOs} PO(s)</span>·
              <span>{fmtCurrency(vendor.totalValue)}</span>·
              <span style={{ color: otdColor, fontWeight: 600 }}>{vendor.otdPercent}% OTD</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => onEdit(vendor)} data-testid="edit-contact-btn">
              Edit Contact
            </Button>
            <button onClick={onClose}><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Contacts */}
          <div>
            <div className="text-[10px] uppercase font-bold mb-2" style={{ color: 'var(--text-muted)' }}>Contacts ({vendor.contacts.length})</div>
            {vendor.contacts.length === 0 ? (
              <div className="p-3 rounded-lg text-xs flex items-center gap-2"
                style={{ background: 'var(--tone-amber-bg)', color: 'var(--tone-amber-text)' }}>
                <AlertTriangle className="w-4 h-4" /> No contact info captured for this vendor yet.
              </div>
            ) : (
              <div className="space-y-2">
                {vendor.contacts.map((c, i) => (
                  <div key={i} className="p-3 rounded-lg border" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}
                    data-testid={`vendor-contact-${i}`}>
                    <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                      <User className="w-3.5 h-3.5" /> {c.name || '— Unnamed contact —'}
                    </div>
                    <div className="mt-1 grid grid-cols-1 gap-1 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                      {c.email && <div className="flex items-center gap-1 cursor-pointer" onClick={() => copy(c.email)}>
                        <Mail className="w-3 h-3" /> {c.email} <Copy className="w-3 h-3 opacity-60" />
                      </div>}
                      {c.phone && <div className="flex items-center gap-1 cursor-pointer" onClick={() => copy(c.phone)}>
                        <Phone className="w-3 h-3" /> {c.phone} <Copy className="w-3 h-3 opacity-60" />
                      </div>}
                    </div>
                    <div className="mt-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                      Referenced by {c.referencedBy.length} MCI(s): {c.referencedBy.slice(0, 4).join(', ')}
                      {c.referencedBy.length > 4 && ` +${c.referencedBy.length - 4} more`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Categories/Disciplines */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase font-bold mb-1" style={{ color: 'var(--text-muted)' }}>Categories</div>
              <div className="flex flex-wrap gap-1">
                {vendor.categories.map(c => (
                  <span key={c} className="text-[10px] px-2 py-0.5 rounded-full"
                    style={{ background: c === 'BULK' ? 'var(--tone-blue-bg)' : 'var(--tone-amber-bg)',
                             color:      c === 'BULK' ? 'var(--tone-blue-text)' : 'var(--tone-amber-text)' }}>{c}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold mb-1" style={{ color: 'var(--text-muted)' }}>Disciplines</div>
              <div className="flex flex-wrap gap-1">
                {vendor.disciplines.map(d => (
                  <span key={d} className="text-[10px] px-2 py-0.5 rounded-full"
                    style={{ background: 'var(--card-bg-alt)', color: 'var(--text-primary)',
                              border: '1px solid var(--border-color)' }}>{d}</span>
                ))}
              </div>
            </div>
          </div>

          {/* MCIs */}
          <div>
            <div className="text-[10px] uppercase font-bold mb-2" style={{ color: 'var(--text-muted)' }}>
              MCIs ({vendor.openMcis.length})
            </div>
            <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
              <table className="w-full text-[11px]">
                <thead>
                  <tr style={{ background: 'var(--card-bg-alt)' }}>
                    <th className="px-2 py-1.5 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>MCI</th>
                    <th className="px-2 py-1.5 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Description</th>
                    <th className="px-2 py-1.5 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>PO</th>
                    <th className="px-2 py-1.5 text-right font-semibold" style={{ color: 'var(--text-muted)' }}>Value</th>
                    <th className="px-2 py-1.5 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {vendor.openMcis.map((m, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border-color)' }}
                      data-testid={`vendor-mci-${m.mciLineNumber}`}>
                      <td className="px-2 py-1.5 font-mono" style={{ color: 'var(--text-primary)' }}>
                        <button onClick={(e) => { e.stopPropagation(); onOpenMci?.(m.mciLineNumber); }}
                          className="hover:underline flex items-center gap-1" style={{ color: 'var(--tone-navy-text)' }}
                          data-testid={`open-mci-${m.mciLineNumber}`}>
                          {m.mciLineNumber} <ExternalLink className="w-2.5 h-2.5" />
                        </button>
                      </td>
                      <td className="px-2 py-1.5 truncate max-w-[220px]" style={{ color: 'var(--text-primary)' }}>
                        {m.description}
                      </td>
                      <td className="px-2 py-1.5 font-mono" style={{ color: 'var(--text-muted)' }}>{m.poNumber || '—'}</td>
                      <td className="px-2 py-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtCurrency(m.poValue)}</td>
                      <td className="px-2 py-1.5">
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold"
                          style={STATUS_COLORS[m.status] || { background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>
                          {m.status || '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VendorContactBook({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('ALL');
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [openMci, setOpenMci] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editVendor, setEditVendor] = useState(null);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const { data } = await api.get(
        `/v1/projects/${projectId}/mci/control-tower/vendor-contact-book?${params}`
      );
      setData(data);
    } catch (e) {
      toast.error('Failed to load vendor contact book');
    }
    setLoading(false);
  }, [projectId, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Filter vendors client-side by material category (client-only — the
  // vendor-contact-book endpoint aggregates across all MCIs and doesn't
  // require a server-side re-query for this small dataset).
  const filteredVendors = useMemo(() => {
    if (!data) return [];
    if (catFilter === 'ALL') return data.vendors || [];
    return (data.vendors || []).filter(v => (v.categories || []).includes(catFilter));
  }, [data, catFilter]);

  const totalContacts = useMemo(() => {
    if (!data) return 0;
    return (data.vendors || []).reduce((s, v) => s + v.contacts.length, 0);
  }, [data]);

  if (loading && !data) {
    return <div className="flex items-center justify-center py-24" data-testid="vendor-book-loading">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="vendor-contact-book">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Vendors',           value: data.count,                  icon: Building2, color: 'var(--tone-navy-text)' },
          { label: 'Total Contacts',    value: totalContacts,               icon: BookUser,  color: '#F26B1F' },
          { label: 'With Contact Info', value: data.vendorsWithContacts,    icon: User,      color: '#16A34A' },
          { label: 'Missing PoC',       value: data.vendorsMissingContacts, icon: AlertTriangle, color: '#DC2626' },
        ].map((k, i) => {
          const Icon = k.icon;
          return (
            <div key={i} className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
              data-testid={`vbook-kpi-${i}`}>
              <div className="flex items-center gap-2 text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>
                <Icon className="w-3.5 h-3.5" style={{ color: k.color }} /> {k.label}
              </div>
              <div className="text-xl font-bold mt-1" style={{ color: k.color }}>{k.value}</div>
            </div>
          );
        })}
      </div>

      {/* Universal Filter Bar */}
      <UniversalFilterBar
        search={search} onSearchChange={setSearch} searchPlaceholder="Search vendor / contact / email…"
        category={catFilter} onCategoryChange={setCatFilter}
        categoryOptions={[{ value:'ALL', label:'ALL' }, { value:'BULK', label:'BULK' }, { value:'TAGGED', label:'TAGGED' }]}
        totalCount={(filteredVendors || []).length} totalLabel="vendors"
        right={
          <div className="flex items-center gap-1">
            <PermissionGate module="MCI" action="create" fallback={null}>
              <Button size="sm" onClick={async () => {
                try {
                  await api.post(`/v1/projects/${projectId}/mci/vendor-master/bootstrap-from-mcis`);
                  toast.success('Vendor master synced from MCIs');
                  fetchData();
                } catch { toast.error('Sync failed'); }
              }} variant="outline" data-testid="vendor-book-bootstrap"
                title="Create canonical vendor records from MCI vendor names">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Sync from MCIs
              </Button>
            </PermissionGate>
            <ExcelWorkflowMenu
              testIdPrefix="vendor-excel"
              endpoints={{
                label: 'Vendors',
                template: `/v1/projects/${projectId}/mci/vendor-master/template.xlsx`,
                templateName: 'vendor-master-template.xlsx',
                importUrl: `/v1/projects/${projectId}/mci/vendor-master/import`,
                exportUrl: `/v1/projects/${projectId}/mci/vendor-master/export.xlsx`,
                exportName: `vendors-${projectId}.xlsx`,
              }}
              onImported={fetchData}
            />
            <PermissionGate module="MCI" action="create" fallback={null}>
              <Button size="sm" onClick={() => { setEditVendor(null); setFormOpen(true); }}
                data-testid="vendor-new-btn"
                className="text-white font-semibold" style={{ background: '#F26B1F' }}>
                <Plus className="w-3.5 h-3.5 mr-1" /> New Vendor
              </Button>
            </PermissionGate>
            <Button size="sm" variant="ghost" onClick={fetchData} data-testid="vendor-book-refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        }
      />

      {/* Vendors table */}
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
        <table className="w-full text-xs">
          <thead>
            <tr style={{ background: 'var(--card-bg-alt)' }}>
              <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Vendor</th>
              <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Primary Contact</th>
              <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Email / Phone</th>
              <th className="px-3 py-2 text-center font-semibold" style={{ color: 'var(--text-muted)' }}>MCIs</th>
              <th className="px-3 py-2 text-center font-semibold" style={{ color: 'var(--text-muted)' }}>POs</th>
              <th className="px-3 py-2 text-right font-semibold" style={{ color: 'var(--text-muted)' }}>Total Value</th>
              <th className="px-3 py-2 text-center font-semibold" style={{ color: 'var(--text-muted)' }}>OTD</th>
              <th className="px-3 py-2 text-center font-semibold" style={{ color: 'var(--text-muted)' }}></th>
            </tr>
          </thead>
          <tbody>
            {(filteredVendors || []).map(v => {
              const c = v.primaryContact;
              const otdColor = v.otdPercent >= 80 ? '#16A34A' : v.otdPercent >= 60 ? '#EA580C' : '#DC2626';
              return (
                <tr key={v.vendor} style={{ borderTop: '1px solid var(--border-color)' }}
                  className="cursor-pointer hover:bg-black/5"
                  onClick={() => setSelected({ ...v, _projectId: projectId })}
                  data-testid={`vendor-row-${v.vendor}`}>
                  <td className="px-3 py-2 font-semibold" style={{ color: 'var(--text-primary)' }}>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-3.5 h-3.5" style={{ color: 'var(--tone-navy-text)' }} /> {v.vendor}
                    </div>
                  </td>
                  <td className="px-3 py-2" style={{ color: 'var(--text-primary)' }}>
                    {c?.name || <span style={{ color: '#DC2626' }}>— missing —</span>}
                  </td>
                  <td className="px-3 py-2 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                    {c?.email || '—'}<br />{c?.phone || ''}
                  </td>
                  <td className="px-3 py-2 text-center font-mono" style={{ color: 'var(--text-primary)' }}>{v.mciCount}</td>
                  <td className="px-3 py-2 text-center font-mono" style={{ color: 'var(--text-muted)' }}>{v.totalPOs}</td>
                  <td className="px-3 py-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtCurrency(v.totalValue)}</td>
                  <td className="px-3 py-2 text-center font-semibold" style={{ color: otdColor }}>
                    {v.otdPercent}%
                  </td>
                  <td className="px-3 py-2 text-center">
                    <ChevronRight className="w-4 h-4 inline" style={{ color: 'var(--text-muted)' }} />
                  </td>
                </tr>
              );
            })}
            {(filteredVendors || []).length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                No vendors found. Add vendor names to MCI entries to build your directory.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <VendorDetailDrawer
          vendor={selected}
          projectId={projectId}
          onClose={() => setSelected(null)}
          onEdit={v => setEditing(v)}
          onOpenMci={setOpenMci}
        />
      )}
      {editing && (
        <PermissionGate module="MCI" action="edit" fallback={null}>
          <ContactEditor
            vendor={editing}
            onClose={() => setEditing(null)}
            onSaved={() => { fetchData(); setSelected(null); }}
          />
        </PermissionGate>
      )}
      {openMci && (
        <MciPackageDrawer projectId={projectId} mci={openMci}
          onClose={() => { setOpenMci(null); fetchData(); }} />
      )}
      {formOpen && (
        <VendorFormDialog projectId={projectId} vendor={editVendor}
          onClose={() => { setFormOpen(false); setEditVendor(null); }}
          onSaved={() => fetchData()} />
      )}
    </div>
  );
}
